/**
 * One prompt group in the ranked list: its rank, name, how many texts it has
 * (and how many rank alone), the formula's values on all its texts
 * summed, the score — and, opened, the same math as a text. A "retire the
 * prompt group" rule (Phase 19) badges it beside its name.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenHourFormula, DropGenRankedGroup } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { excludeHit } from '../../../../rules/rule-model';
import { RuleHitBadges, RuleHitList } from '../../../../rules/RuleHits';
import { TagChips } from '../../../../rules/TagChips';
import type { RankingColumn } from '../ranking-columns';
import { rowGrid } from '../results-grid';
import { ScoreDetail } from '../ScoreDetail';
import { ExpandButton, MetricCells, ScoreCell } from './RowCells';

const fmt = (n: number) => n.toLocaleString('en-US');

interface Props {
  g: DropGenRankedGroup;
  columns: RankingColumn[];
  formula: DropGenHourFormula;
  bar: number;
  open: boolean;
  onOpen: () => void;
  floor: { minVolume: number | null; rangeLabel: string; primaryRangeKey: string };
}

const FOOTNOTE =
  "A prompt group is scored on all its texts together — their counts summed, their rates recomputed from the sums — on the same formula and Exclude rules as a text. That is why a group can rank while none of its texts ranks alone.";

export const GroupRow: FC<Props> = ({ g, columns, formula, bar, open, onOpen, floor }) => (
  <li className={cn(open && 'bg-primary/5')}>
    <div className={cn(rowGrid(columns.length), 'px-3 py-2')}>
      <span className="font-mono text-xs tabular-nums text-muted-foreground">{g.rank ? `#${g.rank}` : '—'}</span>
      <button type="button" onClick={onOpen} className="min-w-0 text-left">
        <p className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium">{g.groupName}</span>
          <RuleHitBadges hits={g.ruleHits} />
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {g.textCount} text{g.textCount === 1 ? '' : 's'} · {g.rankedCount} ranked alone · {fmt(g.uniqueVisits)} unique visits · {fmt(g.conversions)} conv.
        </p>
        {g.tags?.length ? (
          <p className="mt-1 flex">
            <TagChips tags={g.tags} max={4} />
          </p>
        ) : null}
      </button>
      <MetricCells columns={columns} metricsByRange={g.metricsByRange} floor={floor} />
      <ScoreCell score={g.score} bar={bar} />
      <div className="flex justify-end">
        <ExpandButton open={open} onClick={onOpen} what="this prompt group" />
      </div>
    </div>
    {open && g.ruleHits?.length ? (
      <div className="px-3 pb-2">
        <RuleHitList hits={g.ruleHits} />
      </div>
    ) : null}
    {open ? (
      <ScoreDetail
        formula={formula}
        breakdown={g.breakdown}
        metricsByRange={g.metricsByRange}
        unrankedNote={`Excluded — ${excludeHit(g.ruleHits)?.reason ?? `no data in ${floor.rangeLabel}`}, all its texts together: not ranked yet.`}
        footnote={FOOTNOTE}
      />
    ) : null}
  </li>
);
